import React from 'react';
import { useMatch, Link } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import FooterBg from '@/assets/images/onboarding/footer-wave.png';
import HandPhone from '@/assets/icons/phone-call-1.svg'
import MoneyHand from '@/assets/icons/money-hand.svg'

import { ROUTES } from '@/shared/constants/routes';
import { onBoardingFixedBlock } from '@/store/slices/onboarding/selectors';
import { useAppSelector } from '@/shared/hooks/store';
import PrivacyChoices from '@/assets/images/privacy-choices.png';

interface Props {
    ivoryBg?: boolean;
}

const links = [
    { label: 'Privacy Policy', link: 'https://medbox.com/privacy-policy/' },
    { label: 'Terms of Use', link: 'https://medbox.com/terms-of-use/' },
    { label: 'Cookie Policy', link: 'https://medbox.com/cookie-policy/' },
    { label: 'Notice of Privacy', link: 'https://medbox.com/notice-of-privacy-practices/' },
    { label: 'Child Safety Waiver', link: 'https://medbox.com/child-safety-waiver/' },
    { label: 'Privacy Choices', link: '#', external: true, customClass: 'cky-banner-element', img: PrivacyChoices }, // feat: adding the revisit classname tagged trigger
];


const Footer = ({ ivoryBg }: Props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isOnboarding = useMatch(`${ROUTES.ONBOARDING.MAIN}/*`);
    const fixedBlock = useAppSelector(onBoardingFixedBlock);
    const height = fixedBlock ? fixedBlock?.offsetHeight : 0;

    return (
        <div className="relative flex flex-col !bg-inherit" id="footer" data-testid="onboarding-footer-container">
            <div className={`flex h-[55px] w-full items-end ${ivoryBg ? 'bg-[#f8f5f0]' : ''}`}>
                <img src={FooterBg} alt="" className="relative z-[1] max-h-[55px] w-full" />
            </div>

            <div
                className="relative bg-primary-700 text-white"
                id="footer"
                style={{
                    paddingBottom: isOnboarding && isMobile ? height + 48 + 'px' : ''
                }}
            >
                <div className="2xl:mt-24 relative z-10 mx-auto mt-12 flex h-full flex-col justify-between gap-[30px] bg-primary-700 px-5 pb-[20px] lg:mt-16 lg:flex-row lg:gap-16 lg:pb-16 xl:w-[1200px] xl:gap-24 xl:px-0 2xl:w-[1300px] 2xl:gap-36 lx:px-8 3xl:w-[1500px] 3xl:gap-48">

                    <div className="flex-1">
                        <div className='flex flex-row justify-start items-start lg:justify-center lg:items-center'>
                            <div className="flex shrink-0 h-[50px] w-[50px] lg:h-[75px] lg:w-[75px] mr-[21px] bg-mint-200 rounded-lg shadow-footerIcon">
                                <img src={HandPhone} alt="Phone icon" className="p-2.5 lg:p-4" />
                            </div>
                            <div className="font-dmSans flex flex-col gap-1 lg:gap-2">
                                <span className="font-dmSansBold text-17 md:text-20" data-testid="onboarding-footer-questions-title">Have Any Questions?</span>
                                <div className="text-15 block md:hidden">
                                    <a target="_blank" href='https://medbox.com/book/' className='underline font-dmSansBold text-15' data-testid="onboarding-footer-questions-call-link">Schedule a Call</a>
                                </div>
                                <div className="text-14 block md:hidden">
                                    <span data-testid="onboarding-footer-questions-description">Speak with a MedBox Care Advisor, and we’ll be happy to assist you with anything you need.</span>
                                </div>
                                <p className="text-14 hidden md:block">
                                    <a target="_blank" href='https://medbox.com/book/' className='underline font-dmSansBold text-15' data-testid="onboarding-footer-questions-call-link">Schedule a Call</a> with a MedBox Care Advisor, and we’ll be happy to assist you with anything you need.
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* <div className="my-8 h-[1px] w-full bg-mint-400 lg:my-0 lg:h-auto lg:w-[1px]"></div> */}

                    <div className="flex-1">
                        <div className='flex flex-row justify-start items-start  lg:justify-center lg:items-center'>
                            <div className="flex shrink-0 h-[50px] w-[50px] lg:h-[75px] lg:w-[75px] mr-[21px] bg-mint-200 rounded-lg shadow-footerIcon">
                                <img src={MoneyHand} alt="Phone icon" className="p-2.5 lg:p-4" />
                            </div>
                            <div className="font-dmSans flex flex-col gap-1 lg:gap-2">
                                <span className="font-dmSansBold text-17 md:text-20" data-testid="onboarding-footer-copays-title">How Much are MedBox Copays?</span>
                                <p className="text-14 " data-testid="onboarding-footer-copays-description">
                                    Your copays will stay the same as they are at your local retail pharmacy for a 30-day supply of medication.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-full border-t border-primary py-[20px]">
                    <div className="flex flex-col 2xl:flex-row  items-start 2xl:items-center justify-center gap-4 text-center p-[10px] text-12 px-[20px] lx:px-[30px] font-atkinson text-mint-400">
                        <span>
                            © 2025 MedBox by AmeriPharma. All Rights Reserved.
                        </span>
                        <div className="flex flex-row flex-wrap justify-start items-center gap-3 lg:gap-0">
                            {links.map((link, index) => (
                                <div key={link.label} className="flex items-center">
                                    {link.external ? (
                                        <a
                                            href={link.link}
                                            className={`flex items-center gap-1 border-b border-transparent hover:border-current ${link.customClass || ''}`}
                                        >
                                            {link.label}
                                            {link.img && (
                                                <img
                                                    src={link.img}
                                                    alt="icon"
                                                    className="w-[22px] h-[12px]"
                                                />
                                            )}
                                        </a>
                                    ) : (
                                        <Link
                                            to={link.link}
                                            className="border-b border-transparent hover:border-current flex items-center gap-1"
                                            target="_blank"
                                        >
                                            {link.label}
                                            {link.img && (
                                                <img
                                                    src={link.img}
                                                    alt={`${link.label} icon`}
                                                    className="w-[22px] h-[12px]"
                                                />
                                            )}
                                        </Link>
                                    )}
                                    {index !== links.length - 1 && (
                                        <span className="hidden lg:inline mx-2 text-white">•</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Footer;
